import { GridCellParams } from '@mui/x-data-grid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useCepList from '../../../routes/AuthenticatedApp/CepList/CepList.hook';
import useCepAdminDashboardListConfig from './CepAdminDashboardList.config';
import { PAGINATION_PAGE_SIZE } from 'basics/constants/common.constant';
import { mayaCepEditRoute } from 'basics/constants/routes.constants';
import useGetAllCepQuery from 'basics/graphql/queries/getAllCeps';
import { CustomerEngagementPlanning, DashboardFilter } from 'generated/maya.types';

const useCepAdminDashboardList = (filters?: DashboardFilter, useAdminFilters = false) => {
  const [customerInitiativeFilter] = useState<(string | number)[]>([]);
  const [statusFilter] = useState<(string | number)[]>([]);
  const [ceps, setCeps] = useState<CustomerEngagementPlanning[]>([]);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGINATION_PAGE_SIZE);
  const [rowCountState, setRowCountState] = useState(0);

  const navigate = useNavigate();

  let queryFilters;
  if (filters) {
    queryFilters = { ...filters, tacticStatus: undefined };
  }

  const queryOptions = useMemo(
    () => ({
      limit: pageSize,
      offset: page * pageSize,
    }),
    [page, pageSize],
  );

  const { data: cepData, loading: cepLoading, count } = useGetAllCepQuery(queryFilters, queryOptions, useAdminFilters);

  useEffect(() => {
    if (filters) {
      setRowCountState((prevRowCountState) => (count !== undefined ? count : prevRowCountState));
    }
  }, [count, filters, setRowCountState]);

  useEffect(() => {
    if (!cepLoading && filters) {
      setCeps(cepData);
    }
  }, [cepData, cepLoading, filters]);

  const {
    setCepToFeedbackCallback,
    setCepToApprovedCallback,
    syncCepCallback,
    editCepCallback,
    duplicateCepCallback,
    openModalCallback,
    confirmationDialogConfig,
    removeCepLoading,
    cancelCepLoading,
    restoreCepLoading,
    open,
    setOpen,
  } = useCepList();

  const {
    cepListColumnConfig,
  } = useCepAdminDashboardListConfig(
    setCepToFeedbackCallback,
    setCepToApprovedCallback,
    syncCepCallback,
    editCepCallback,
    duplicateCepCallback,
    openModalCallback,
  );

  const getFilteredRows = useCallback(() => {
    let filteredRows = ceps;

    if (customerInitiativeFilter.length > 0) {
      filteredRows = cepData.filter((value) => {
        if (typeof value.customerInitiative === 'string') {
          return value.customerInitiative;
        }

        return customerInitiativeFilter.indexOf(value.customerInitiative._id) !== -1;
      });
    }

    if (statusFilter.length > 0) {
      filteredRows = filteredRows.filter((value) => {
        return statusFilter.indexOf(value.status) !== -1;
      });
    }

    return filteredRows;
  }, [ceps, customerInitiativeFilter, statusFilter, cepData]);

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const onPageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  const onCellClick = (params: GridCellParams) => {
    navigate(`${mayaCepEditRoute}?id=${params.row._id}`);
  };

  return {
    cepListColumnConfig,
    customerInitiativeFilter,
    getFilteredRows,
    statusFilter,
    cepData,
    cepLoading,
    onPageChange,
    onPageSizeChange,
    rowCountState,
    onCellClick,
    open,
    confirmationDialogConfig,
    removeCepLoading,
    cancelCepLoading,
    restoreCepLoading,
    setOpen,
  };
};

export default useCepAdminDashboardList;
